import api from './api';
import {
  AcceptInvitationRequest,
  AcceptInvitationResponse,
  CompanyNameStatusResponse,
  ConfirmRegistrationRequest,
  InvitationAdminResponse,
  InvitationStatus,
  InvitationTokenResponse,
  EmailStatusResponse,
  InviteEmployeeRequest,
  RegisterTenantRequest,
  RegisterTenantResponse,
  RequestOtpRequest,
  TaxIdStatusResponse,
  ValidateInviteTokenResponse,
} from '../types';

type WrappedApiResponse<T> = {
  success: boolean;
  data: T;
  message?: string;
};

const unwrapApiPayload = <T>(payload: unknown): T => {
  if (
    payload !== null &&
    typeof payload === 'object' &&
    'success' in (payload as Record<string, unknown>) &&
    'data' in (payload as Record<string, unknown>)
  ) {
    const wrapped = payload as WrappedApiResponse<T>;
    if (!wrapped.success) {
      throw new Error(wrapped.message || 'Error en la operación');
    }
    return wrapped.data;
  }

  return payload as T;
};

export const tenantService = {
  async inviteUser(
    data: InviteEmployeeRequest,
  ): Promise<InvitationTokenResponse> {
    const response = await api.post('/tenants/invite', data);
    return unwrapApiPayload<InvitationTokenResponse>(response.data);
  },

  async inviteEmployee(
    data: InviteEmployeeRequest,
  ): Promise<InvitationTokenResponse> {
    return tenantService.inviteUser(data);
  },

  async getInvitations(
    status?: InvitationStatus,
  ): Promise<InvitationAdminResponse[]> {
    const response = await api.get('/tenants/invitations', {
      params: { ...(status ? { status } : {}), size: 500, sort: 'createdAt,desc' },
    });
    const data = unwrapApiPayload<{ content: InvitationAdminResponse[] } | InvitationAdminResponse[]>(response.data);
    return Array.isArray(data) ? data : (data?.content ?? []);
  },

  async cancelInvitation(id: number): Promise<InvitationAdminResponse> {
    const response = await api.post(`/tenants/invitations/${id}/cancel`);
    return unwrapApiPayload<InvitationAdminResponse>(response.data);
  },

  async resendInvitation(id: number): Promise<InvitationAdminResponse> {
    const response = await api.post(`/tenants/invitations/${id}/resend`);
    return unwrapApiPayload<InvitationAdminResponse>(response.data);
  },

  async registerTenant(
    data: RegisterTenantRequest,
  ): Promise<RegisterTenantResponse> {
    const response = await api.post('/tenants/register', data);
    return unwrapApiPayload<RegisterTenantResponse>(response.data);
  },

  async getEmailStatus(email: string): Promise<EmailStatusResponse> {
    const response = await api.get('/tenants/email-status', {
      params: { email },
    });
    return unwrapApiPayload<EmailStatusResponse>(response.data);
  },

  async getCompanyNameStatus(name: string): Promise<CompanyNameStatusResponse> {
    const response = await api.get('/tenants/company-name-status', {
      params: { name },
    });
    return unwrapApiPayload<CompanyNameStatusResponse>(response.data);
  },

  async getTaxIdStatus(taxId: string): Promise<TaxIdStatusResponse> {
    const response = await api.get('/company/tax-id-status', {
      params: { taxId },
    });
    return unwrapApiPayload<TaxIdStatusResponse>(response.data);
  },

  async acceptInvitation(
    data: AcceptInvitationRequest,
  ): Promise<AcceptInvitationResponse> {
    const response = await api.post('/tenants/accept-invite', data);
    return unwrapApiPayload<AcceptInvitationResponse>(response.data);
  },

  // ── OTP ──────────────────────────────────────────────────────────

  /** Paso 1 del registro: solicita el OTP al email (respuesta siempre genérica) */
  async requestOtp(data: RequestOtpRequest): Promise<{ message: string }> {
    const response = await api.post('/tenants/register/request-otp', data);
    return response.data;
  },

  /** Paso 2 del registro: envía todos los datos + OTP para completar el registro */
  async confirmRegistration(
    data: ConfirmRegistrationRequest,
  ): Promise<RegisterTenantResponse> {
    const response = await api.post('/tenants/register/confirm', data);
    return unwrapApiPayload<RegisterTenantResponse>(response.data);
  },

  /**
   * Valida el token del link de invitación y dispara el envío del OTP al email del invitado.
   * Retorna el email enmascarado y el nombre del tenant.
   */
  async validateInviteToken(token: string): Promise<ValidateInviteTokenResponse> {
    const response = await api.get('/tenants/accept-invite/validate', {
      params: { token },
    });
    return unwrapApiPayload<ValidateInviteTokenResponse>(response.data);
  },
};
